<template>
  <v-row row wrap>
    <v-col cols="12" sm="6" md="4" offset-md="8" offset-sm="6">
      <search-input @search="performSearch" v-model="search" />
    </v-col>

    <v-col cols="12">
      <div class="table-fixed-header-wrap">
      <v-data-table
        class="mt-3"
        fixed-header
        height="calc(100vh - 260px)"
        :headers="headers"
        :items="items"
        :search="search"
        :single-expand="false"
        :server-items-length="totalItems"
        :loading="loading"
        :page.sync="pageNumber"
        :items-per-page.sync="itemsPerPage"
        :sort-by.sync="orderBy"
        :sort-desc.sync="orderDesc"
        :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
        @update:page="fetch"
        @update:sort-by="fetch"
        @update:sort-desc="fetch"
        @update:items-per-page="fetch"
      >
        <template v-slot:item.tipo="{ item }">
          <v-chip small :color="getTipoColor(item.tipo)">
            {{ getTipoLabel(item.tipo) }}
          </v-chip>
        </template>

        <template v-slot:item.valor="{ item }">
          <span v-if="item.tipo === 'porcentaje'">{{ item.valor }}%</span>
          <span v-else-if="item.tipo === 'por_cantidad' && item.valor != null">
            {{ item.valor }}%
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.productos="{ item }">
          <span v-if="item.productos && item.productos.length > 0">
            {{ item.productos.length }} {{ $t('oferta.labels.productos') }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.deposito="{ item }">
          {{ item.deposito ? item.deposito.nombre : '-' }}
        </template>

        <template v-slot:item.diasSemana="{ item }">
          <span v-if="item.diasSemana && item.diasSemana.length > 0">
            {{ item.diasSemana.length === 7 ? $t('oferta.labels.todosLosDias') : item.diasSemana.length + ' d?as' }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.fechaInicio="{ item }">
          {{ item.fechaInicio ? getDateTimeFormat(item.fechaInicio) : '-' }}
        </template>

        <template v-slot:item.fechaFin="{ item }">
          {{ item.fechaFin ? getDateTimeFormat(item.fechaFin) : $t('oferta.labels.sinFechaLimite') }}
        </template>

        <template v-slot:item.mediosPago="{ item }">
          <span v-if="item.mediosPago && item.mediosPago.length > 0">
            {{ item.mediosPago.join(', ') }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.activa="{ item }">
          <v-chip small :color="item.activa ? 'green' : 'red'" dark>
            {{ item.activa ? $t('oferta.labels.activa') : $t('oferta.labels.inactiva') }}
          </v-chip>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ getDateTimeFormat(item.createdAt) }}
        </template>

        <template slot="no-data">
          <div class="text-xs-center" v-t="'common.noData'"></div>
        </template>

        <template slot="loading">
          <div class="text-xs-center" v-t="'common.loading'"></div>
        </template>

        <template v-slot:item.action="{ item }">
          <show-button @click="$emit('show', item)" />
          <edit-button @click="$emit('update', item)" />
          <delete-button @click="$emit('delete', item)" />
        </template>
      </v-data-table>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import OfertaProvider from "../../../providers/OfertaProvider";
import UserConfigurationProvider from "../../../../user-configuration/providers/UserConfigurationProvider";
import { EditButton, ShowButton, DeleteButton, SearchInput } from "@dracul/common-frontend";
import { DayjsMixin } from "@dracul/dayjs-frontend";

export default {
  name: "OfertaList",
  components: { EditButton, ShowButton, DeleteButton, SearchInput },
  mixins: [DayjsMixin],
  data() {
    return {
      items: [],
      totalItems: null,
      loading: false,
      orderBy: "createdAt",
      orderDesc: true,
      itemsPerPage: 5,
      pageNumber: 1,
      search: "",
      filters: [],
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("oferta.labels.nombre"), value: "nombre" },
        { text: this.$t("oferta.labels.tipo"), value: "tipo" },
        { text: this.$t("oferta.labels.valor"), value: "valor" },
        { text: this.$t("oferta.labels.productos"), value: "productos" },
        { text: this.$t("oferta.labels.deposito"), value: "deposito" },
        { text: this.$t("oferta.labels.diasSemana"), value: "diasSemana" },
        { text: this.$t("oferta.labels.fechaInicio"), value: "fechaInicio" },
        { text: this.$t("oferta.labels.fechaFin"), value: "fechaFin" },
        { text: this.$t("oferta.labels.activa"), value: "activa" },
        { text: this.$t("common.createdAt"), value: "createdAt" },
        { text: this.$t("common.actions"), value: "action", sortable: false },
      ];
    },
    getOrderBy() {
      return Array.isArray(this.orderBy) ? this.orderBy[0] : this.orderBy;
    },
    getOrderDesc() {
      return Array.isArray(this.orderDesc) ? this.orderDesc[0] : this.orderDesc;
    },
  },
  async created() {
    await this.updateDepositoFilter();
    this.fetch();
  },
  mounted() {
    // Escuchar eventos de actualizaci?n de configuraci?n
    this.$root.$on('userConfigurationUpdated', async () => {
      await this.updateDepositoFilter();
      this.fetch();
    });
  },
  beforeDestroy() {
    this.$root.$off('userConfigurationUpdated');
  },
  methods: {
    async updateDepositoFilter() {
      // Intentar obtener depositoId del cache de Apollo
      let depositoId = UserConfigurationProvider.getDepositoIdFromCache();
      
      // Si no est? en el cache, intentar cargar la configuraci?n del usuario
      if (!depositoId) {
        try {
          const response = await UserConfigurationProvider.getUserConfiguration(false);
          if (response && response.data && response.data.userConfigurationGet) {
            depositoId = response.data.userConfigurationGet.depositoIdSeleccionado;
          }
        } catch (error) {
          // Si falla, intentar con network-only
          try {
            const response = await UserConfigurationProvider.getUserConfiguration(true);
            if (response && response.data && response.data.userConfigurationGet) {
              depositoId = response.data.userConfigurationGet.depositoIdSeleccionado;
            }
          } catch (err) {
            console.error('Error loading user configuration:', err);
          }
        }
      }
      
      // Buscar o crear el filtro de deposito
      let depositoFilterIndex = this.filters.findIndex(f => f.field === 'deposito' || f.field === 'depositoId');
      
      if (depositoId) {
        const idStr = typeof depositoId === 'object' && depositoId?.id ? depositoId.id : (depositoId?.toString?.() || depositoId);
        if (depositoFilterIndex >= 0) {
          // Actualizar el filtro existente
          this.filters[depositoFilterIndex].value = idStr;
          this.filters[depositoFilterIndex].operator = '$eq';
          this.filters[depositoFilterIndex].field = 'deposito';
        } else {
          // Agregar nuevo filtro
          this.filters.push({
            field: 'deposito',
            operator: '$eq',
            value: idStr
          });
        }
      } else {
        // Si no hay depositoId, remover el valor del filtro si existe
        if (depositoFilterIndex >= 0) {
          this.filters[depositoFilterIndex].value = null;
        }
      }
    },
    performSearch() {
      this.pageNumber = 1;
      this.fetch();
    },
    async fetch() {
      // Asegurar que el filtro de dep?sito siempre est? presente antes de hacer la consulta
      await this.updateDepositoFilter();
      this.loading = true;
      OfertaProvider.paginateOfertas(
        this.pageNumber,
        this.itemsPerPage,
        this.search,
        this.filters,
        this.getOrderBy,
        this.getOrderDesc
      )
        .then((r) => {
          this.items = r.data.ofertaPaginate.items;
          this.totalItems = r.data.ofertaPaginate.totalItems;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => (this.loading = false));
    },
    getTipoLabel(tipo) {
      return this.$t(`oferta.labels.${tipo}`);
    },
    getTipoColor(tipo) {
      const colors = {
        porcentaje: "blue",
        por_cantidad: "teal",
        "2x1": "green",
        "3x2": "orange",
        "4x3": "purple",
      };
      return colors[tipo] || "grey";
    },
  },
};
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}
</style>
